import { trackChunkErrorReload } from "@/lib/posthog-events"

const CHUNK_ERROR_PATTERN = /Failed to load chunk|Loading chunk|ChunkLoadError|from module \d+/i

/** Fallback guard window when the inline watchdog is absent (5 min, then retry allowed). */
export const CHUNK_RELOAD_GUARD_MS = 5 * 60 * 1000

/**
 * True if `error` looks like a Turbopack/webpack chunk-load failure.
 *
 * Turbopack throws `Error: Failed to load chunk <path> from module <id>` with
 * `name === "Error"`, so we match by message rather than constructor name.
 * Webpack's classic `ChunkLoadError` is matched by either.
 */
export function isChunkLoadError(error: Error | undefined | null): boolean {
  if (!error) return false
  if (error.name === "ChunkLoadError") return true
  const msg = error.message || ""
  return CHUNK_ERROR_PATTERN.test(msg)
}

/** The `/_next/static/...` path named in a chunk error message, or "". */
export function chunkUrlFromMessage(message: string | undefined | null): string {
  return (String(message || "").match(/\/_next\/static\/[^\s"')]+/) || [""])[0]
}

/**
 * Recover from a chunk-load failure (stale chunk after a deploy, or a chunk that
 * 404s during the swap).
 *
 * Preferred path (task-168): hand it to the inline boot watchdog
 * (`window.__gcRecover`, lib/boot-watchdog.ts). It probes the failed chunk with
 * `cache:'reload'` until it returns 200 (backoff 2 → 60 s) and only then reloads,
 * capped at 10 reloads/hour — so a gap that outlasts one reload still heals, and a
 * permanent 404 cannot loop.
 *
 * Fallback (watchdog missing): one immediate reload per pathname per
 * CHUNK_RELOAD_GUARD_MS. The guard used to be a plain flag that never expired, so
 * a tab that failed once stayed blank for the rest of the session.
 */
export function recoverFromChunkError(error: Error, source: string): void {
  if (typeof window === "undefined") return

  const pathname = window.location.pathname

  try {
    trackChunkErrorReload(source, error.message, pathname)
  } catch {
    // Telemetry must never block recovery.
  }

  if (typeof window.__gcRecover === "function") {
    try {
      window.__gcRecover("chunk-boundary", chunkUrlFromMessage(error.message) || undefined)
      return
    } catch {
      // fall through to the plain reload
    }
  }

  const key = `chunk-reload:${pathname}`
  try {
    const last = Number(window.sessionStorage.getItem(key) || 0)
    if (last && Date.now() - last < CHUNK_RELOAD_GUARD_MS) {
      // Reloaded for this URL moments ago and it failed again: stop here, the
      // boundary shows its "Updating…" state and the next attempt is allowed once
      // the guard expires.
      return
    }
    window.sessionStorage.setItem(key, String(Date.now()))
  } catch {
    // sessionStorage can throw in private modes / strict cookie policies.
    // Without a persistent guard a reload could loop, so don't reload.
    return
  }

  console.log(`[${source}] Chunk load failed, reloading:`, error.message)
  window.location.reload()
}
